<template>
	<div>
		<component
			v-if="clientModel.isDateObjectComponent"
			:dateObject.sync="syncDateObject"
			:minutes.sync="syncMinutes"
			v-bind="{ ...clientModel.__bind, ...$attrs }"
			v-on="computedListeners"
			:is="clientModel.__component"
			outlined
			dense
		>
		</component>
		<component
			v-else
			v-model="item"
			v-bind="{ ...clientModel.__bind, ...$attrs }"
			v-on="computedListeners"
			:is="clientModel.__component"
			outlined
			dense
		>
			<!-- Forward all named slots -->
			<template v-for="slot of Object.keys($slots)" :slot="slot">
				<slot :name="slot" />
			</template>
			<!-- Forward all scoped slots -->
			<template v-for="slot of Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
				<slot :name="slot" v-bind="scope" />
			</template>
		</component>
	</div>
</template>

<script>
import { _BaseModel } from '@/plugins/clientModels/models/helpers/_BaseModel';
import LAutocomplete from '@/components/base/LAutocomplete.vue';
import ModelAutocomplete from '@/components/models/autocompletes/ModelAutocomplete.vue';
import ModelAutocompleteAddNew from '@/components/models/autocompletes/ModelAutocompleteAddNew.vue';
import DatePicker from '@/components/base/datePicker/DatePicker.vue';
import SimpleTimecomplete from '@/components/base/simpleTimecomplete/SimpleTimecomplete.vue';
import DateTimePicker from '@/components/base/dateTimePicker/DateTimePicker.vue';
import { VTextField, VTextarea, VCheckbox } from 'vuetify/lib';
import LRender from '@/components/base/LRender.vue';

export default {
	name: 'LInput',
	components: {
		LRender,
		LAutocomplete,
		ModelAutocomplete,
		ModelAutocompleteAddNew,
		VTextField,
		VTextarea,
		VCheckbox,
		DatePicker,
		SimpleTimecomplete,
		DateTimePicker,
	},
	props: {
		value: {
			type: _BaseModel,
			required: false,
		},
		__path: {
			type: String,
			required: false,
		},
		__model: {
			type: _BaseModel,
			required: false,
		},
		__label: {
			type: String,
			required: false,
		},
		__component: {
			type: String,
			required: false,
		},
		__isDate: {
			type: Boolean,
			default: false,
		},
		__isDateTime: {
			type: Boolean,
			default: false,
		},
		__isTime: {
			type: Boolean,
			default: false,
		},
	},
	data: () => ({
		clientModel: {},
	}),
	computed: {
		item: {
			get() {
				if (this.__path) return this.$_.get(this.value, this.__path);
				else return this.value;
			},
			set(value) {
				// mutating prop here although we should never do this
				// was needed to boost performance
				if (this.__path) {
					this.$_.set(this.value, this.__path, value);
					this.$emit('input', this.value);
				} else {
					this.$emit('input', value);
				}
			},
		},
		computedListeners() {
			// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
			const { input, ...otherListeners } = this.$listeners;
			return otherListeners;
		},
		syncDateObject: {
			get() {
				if (this.item instanceof Date) return this.item;
				else return null;
			},
			set(value) {
				if (value instanceof Date) this.item = value;
			},
		},
		syncMinutes: {
			get() {
				if (Number.isInteger(this.item)) return this.item;
				else return null;
			},
			set(value) {
				if (Number.isInteger(this.item)) this.item = value;
			},
		},
	},
	methods: {},
	created() {
		let model = null;
		if (this.__model) model = this.__model;
		else if (this.value && this.value instanceof _BaseModel) model = this.value.constructor;
		// else throw new Error('Neither "model" prop set, nor value provided to derive model from.')

		let pathOptions = null;
		if (model) {
			pathOptions = model.getPath(this.__path);
			// console.log(model.toString(), this.__path, pathOptions);
		}
		// if (!pathOptions) console.log('No pathOptions for path ', this.__path);

		this.clientModel = {
			__bind: this.$_.get(pathOptions, '__bind', {}),
		};
		if (this.__label) this.clientModel.__bind.label = this.__label;
		let component = this.$_.get(pathOptions, '__component', 'div');
		if (this.__component) component = this.__component;
		if (this.$attrs['item-text']) this.clientModel.__bind.itemText = this.$attrs['item-text'];

		let isDate = this.$_.get(pathOptions, '__isDate', !!this.syncDateObject);
		let isDateTime = this.$_.get(pathOptions, '__isDateTime', false);
		let isTime = this.$_.get(pathOptions, '__isTime', false);
		if (this.__isDate) {
			isDate = true;
			isDateTime = false;
			isTime = false;
		}
		if (this.__isDateTime) {
			isDate = false;
			isDateTime = true;
			isTime = false;
		}
		if (this.__isTime) {
			isDate = false;
			isDateTime = false;
			isTime = true;
		}

		if (isDate) component = 'date-picker';
		else if (isDateTime) component = 'date-time-picker';
		else if (isTime) component = 'simple-timecomplete';

		if (this.__component) component = this.__component;

		this.clientModel.__component = component;
		this.clientModel.pathOptions = pathOptions;

		if (['date-picker', 'simple-timecomplete'].includes(component)) this.clientModel.isDateObjectComponent = true;
	},
};
</script>
